/* global axios */

const knowledgeProxy = (accountId, path) =>
  `/api/v1/accounts/${accountId}/proxy/knowledge${path}`;

export const state = {
  schedules: [],
  selectedSchedule: null,
  pagination: {
    page: 1,
    total: 0,
    next: null,
    page_size: 10,
  },
  filters: {
    branch_id: null,
    schedule_id: null,
  },
};

export const getters = {
  formattedPagination: (state) => {
    const { page, total, next, page_size } = state.pagination;
    return {
      currentPage: page,
      totalPages: Math.max(1, Math.ceil(total / page_size)),
      nextPage: next,
      pageSize: page_size,
    };
  },
};

export const mutations = {
  SET_SCHEDULES(state, schedules) { state.schedules = schedules; },
  SET_SELECTED_SCHEDULE(state, schedule) { state.selectedSchedule = schedule; },
  SET_PAGINATION(state, pagination) { state.pagination = { ...state.pagination, ...pagination }; },
  SET_FILTERS(state, filters) { state.filters = { ...state.filters, ...filters }; },
};

export const actions = {
  async fetchSchedules({ commit, rootGetters, state }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.get(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/schedules`),
        {
          params: {
            branch_id: state.filters.branch_id,
            page: state.pagination.page,
            page_size: state.pagination.page_size,
          },
        }
      );

      const results = res.data;
      commit('SET_SCHEDULES', results);
      return results;
    } catch (error) {
      console.error('Fetch schedules failed:', error);
      throw error.response?.data || error;
    }
  },

  async createSchedule({ rootGetters, state }, scheduleData) {
    const accountId = rootGetters.getCurrentAccountId;
    const { branch_id } = state.filters;
    if (!branch_id) throw new Error('Branch ID is required to create schedule');

    try {
      const res = await axios.post(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/branches/${branch_id}/schedules`),
        scheduleData
      );
      return res.data;
    } catch (error) {
      console.error('Create schedule failed:', error);
      throw error.response?.data || error;
    }
  },

  async updateSchedule({ rootGetters }, { scheduleId, scheduleData }) {
    const accountId = rootGetters.getCurrentAccountId;
    if (!scheduleId) throw new Error('Schedule ID is required');

    try {
      const res = await axios.patch(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/schedules/${scheduleId}`),
        scheduleData
      );
      return res.data;
    } catch (error) {
      console.error('Update schedule failed:', error);
      throw error.response?.data || error;
    }
  },

  async deleteSchedule({ rootGetters }, scheduleId) {
    const accountId = rootGetters.getCurrentAccountId;
    if (!scheduleId) throw new Error('Schedule ID is required');

    try {
      const res = await axios.delete(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/schedules/${scheduleId}`)
      );
      return res.data;
    } catch (error) {
      console.error('Delete schedule failed:', error);
      throw error.response?.data || error;
    }
  },

  async fetchSchedule({ commit, rootGetters }, scheduleId) {
    const accountId = rootGetters.getCurrentAccountId;
    if (!scheduleId) throw new Error('Schedule ID is required');

    try {
      const res = await axios.get(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/schedules/${scheduleId}`)
      );
      commit('SET_SELECTED_SCHEDULE', res.data);
      return res.data;
    } catch (error) {
      console.error('Fetch schedule failed:', error);
      throw error.response?.data || error;
    }
  },

  async fetchAllSchedules({ rootGetters, state, commit }) {
    const accountId = rootGetters.getCurrentAccountId;
    const allSchedules = [];
    const pageSize = 100;
    let page = 1;

    try {
      while (true) {
        const res = await axios.get(
          knowledgeProxy(accountId, `/v2/accounts/${accountId}/schedules`),
          {
            params: {
              page,
              page_size: pageSize,
              branch_id: state.filters.branch_id,
            },
          }
        );

        const results = res.data;
        if (!results.length) break;

        allSchedules.push(...results);
        page++;
      }

      commit('SET_SCHEDULES', allSchedules);
      return allSchedules;
    } catch (error) {
      console.error('Fetch all schedules failed:', error);
      throw error.response?.data || error;
    }
  },

  updatePagination({ commit, dispatch, state }, newPagination = {}) {
    commit('SET_PAGINATION', { ...state.pagination, ...newPagination });
    return dispatch('fetchSchedules');
  },

  async updateFilters({ commit, dispatch }, newFilters) {
    commit('SET_FILTERS', newFilters);
    commit('SET_PAGINATION', { page: 1 });
    return dispatch('fetchSchedules');
  },

  async clearFilters({ commit, dispatch }) {
    commit('SET_FILTERS', { branch_id: null, schedule_id: null });
    commit('SET_PAGINATION', { page: 1 });
    return dispatch('fetchSchedules');
  },

  async createWorkingHours({ rootGetters }, { scheduleId, payload }) {
    const accountId = rootGetters.getCurrentAccountId;
    if (!scheduleId) throw new Error('Schedule ID is required');

    try {
      const res = await axios.post(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/schedules/${scheduleId}/working-hours`),
        payload
      );
      return res.data;
    } catch (error) {
      console.error('Create working hours failed:', error);
      throw error.response?.data || error;
    }
  },

  async fetchWorkingHours({ rootGetters }, scheduleId) {
    const accountId = rootGetters.getCurrentAccountId;
    if (!scheduleId) throw new Error('Schedule ID is required');

    try {
      const res = await axios.get(
        knowledgeProxy(accountId, `/accounts/${accountId}/schedules/${scheduleId}/working-hours`)
      );
      return res.data;
    } catch (error) {
      console.error('Fetch working hours failed:', error);
      throw error.response?.data || error;
    }
  },

  async deleteWorkingHours({ rootGetters }, { scheduleId, workingHoursId }) {
    const accountId = rootGetters.getCurrentAccountId;
    if (!scheduleId || !workingHoursId)
      throw new Error('Schedule ID and Working Hours ID are required');

    try {
      const res = await axios.delete(
        knowledgeProxy(accountId, `/accounts/${accountId}/schedules/${scheduleId}/working-hours/${workingHoursId}`)
      );
      return res.data;
    } catch (error) {
      console.error('Delete working hours failed:', error);
      throw error.response?.data || error;
    }
  },

  async editWorkingHours({ rootGetters }, { scheduleId, workingHoursId, payload }) {
    const accountId = rootGetters.getCurrentAccountId;
    if (!scheduleId || !workingHoursId)
      throw new Error('Schedule ID and Working Hours ID are required');

    try {
      const res = await axios.put(
        knowledgeProxy(accountId, `/accounts/${accountId}/schedules/${scheduleId}/working-hours/${workingHoursId}`),
        payload
      );
      return res.data;
    } catch (error) {
      console.error('Edit working hours failed:', error);
      throw error.response?.data || error;
    }
  },

  async toggleSchedule({ rootGetters }, { branchId, scheduleId, toggle }) {
    const accountId = rootGetters.getCurrentAccountId;
    if (!branchId || !scheduleId || !toggle)
      throw new Error('Branch ID, Schedule ID and toggle action are required');

    try {
      const res = await axios.put(
        knowledgeProxy(accountId, `/accounts/${accountId}/branches/${branchId}/schedules/${scheduleId}/${toggle}`),
        {}
      );
      return res.data;
    } catch (error) {
      console.error('Toggle schedule failed:', error);
      throw error.response?.data || error;
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
